// Fix 1: the buy button never goes quiet. Once the cart holds every chair of this kind
// that's in stock (for a one-off chair, the one chair), the main button and the black sticky
// bar say so and open the cart instead of asking Shopify for one more, which it refuses.
// While more are in stock, a tap adds another and the cart opens as usual. If an add fails
// for any other reason, the message also shows on the sticky bar, where the shopper tapped.
// Fix 10: the "Kjøp nå" button next to it adds the chair and goes straight to checkout.
if (!customElements.get('mg-buy-button')) {
  customElements.define(
    'mg-buy-button',
    class MgBuyButton extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.sectionId;
        this.form = document.getElementById(`product-form-${id}`);
        this.mainButton = document.getElementById(`ProductSubmitButton-${id}`);
        this.stickyBar = document.getElementById(`PdpStickyBar-${id}`);
        this.stickyButton = document.getElementById(`PdpStickyAtc-${id}`);
        if (!this.form || !this.mainButton) return;

        // "variantId:quantity," pairs. Stock lists only variants that can't be oversold.
        this.stock = this.parsePairs(this.dataset.stock);
        this.cartQuantities = this.parsePairs(this.dataset.cartQuantities);
        // Capture phase, so it runs before the form submits; the sticky bar clicks this button too.
        this.mainButton.addEventListener('click', (event) => this.onBuyClick(event), true);
        this.unsubscribers = [
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart()),
          subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(() => this.render())),
          subscribe(PUB_SUB_EVENTS.cartError, (event) => this.onCartError(event)),
        ];
        // Fix 10: "Kjøp nå". Delegated, because a variant change can re-render the buttons.
        this.onDocumentClick = (event) => {
          const button = event.target.closest(`#ProductBuyNow-${id}`);
          if (button) this.buyNow(button);
        };
        document.addEventListener('click', this.onDocumentClick);
        // Back from checkout, the browser can restore this page as it was before "Kjøp nå" added
        // the chair. Catch up with the cart so the button and the drawer are right.
        this.onPageShow = (event) => {
          if (!event.persisted) return;
          this.refreshCart();
          this.redrawCart();
        };
        window.addEventListener('pageshow', this.onPageShow);
        this.render();
      }

      disconnectedCallback() {
        this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('pageshow', this.onPageShow);
      }

      // Adds the chair and goes straight to checkout. When the cart already holds all of
      // this chair's stock, it goes to checkout without adding. If Shopify refuses the add
      // but the chair is in the cart, checkout opens anyway; otherwise the error shows where
      // the main button's errors do.
      async buyNow(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        button.querySelector('.loading__spinner')?.classList.remove('hidden');
        try {
          if (!this.cartFull) {
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = new FormData(this.form);
            const response = await fetch(window.routes.cart_add_url, config);
            if (!response.ok) {
              const data = await response.json().catch(() => ({}));
              await this.refreshCart();
              if (!this.cartQuantities.get(this.variantId)) throw new Error(data.description || data.message || '');
            }
          }
          window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        } catch (error) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
          this.form.closest('product-form')?.handleErrorMessage(error.message || window.cartStrings?.error);
        }
      }

      parsePairs(text = '') {
        const pairs = new Map();
        for (const pair of text.split(',').filter(Boolean)) {
          const [variantId, quantity] = pair.split(':');
          pairs.set(variantId, (pairs.get(variantId) || 0) + Number(quantity));
        }
        return pairs;
      }

      get variantId() {
        return String(this.form.querySelector('[name="id"]')?.value);
      }

      get cartFull() {
        const inCart = this.cartQuantities.get(this.variantId) || 0;
        const stock = this.stock.get(this.variantId);
        return inCart > 0 && stock !== undefined && inCart >= stock;
      }

      onBuyClick(event) {
        if (!this.cartFull) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openCart();
      }

      // The drawer is drawn with the page, so it can hold an older cart than Shopify's (the chair
      // went in from another tab, or before the back button). Redraw it, then open it.
      async openCart() {
        this.hideStickyMessage();
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) {
          window.location = window.routes.cart_url;
          return;
        }
        await this.redrawCart();
        drawer.open(this.mainButton);
      }

      async redrawCart() {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        try {
          const ids = drawer.getSectionsToRender().map((section) => section.id);
          const sections = await (await fetch(`${window.routes.cart_url}?sections=${ids.join(',')}`)).json();
          drawer.getSectionsToRender().forEach((section) => {
            const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
            if (target && sections[section.id]) {
              target.innerHTML = drawer.getSectionInnerHTML(sections[section.id], section.selector);
            }
          });
          const fresh = drawer.getSectionDOM(sections['cart-drawer'], 'cart-drawer');
          drawer.classList.toggle('is-empty', !!fresh?.classList.contains('is-empty'));
          drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
        } catch (error) {
          // Opening the drawer as it is beats not opening it.
        }
      }

      async refreshCart() {
        const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
        this.cartQuantities = new Map();
        for (const item of cart.items) {
          const variantId = String(item.variant_id);
          this.cartQuantities.set(variantId, (this.cartQuantities.get(variantId) || 0) + item.quantity);
        }
        this.render();
      }

      async onCartError(event) {
        if (event.source !== 'product-form' || String(event.productVariantId) !== this.variantId) return;
        await this.refreshCart();
        const inCart = this.cartQuantities.get(this.variantId) || 0;
        if (inCart > 0) {
          // Shopify refused one more (stock changed, or the page was out of date): treat
          // what's in the cart as the limit and show the cart.
          this.stock.set(this.variantId, inCart);
          this.render();
          this.form.closest('product-form')?.handleErrorMessage();
          this.openCart();
        } else if (this.stickyBar?.classList.contains('is-visible')) {
          this.showStickyMessage(event.message || event.errors);
        }
      }

      showStickyMessage(text) {
        if (!text || !this.stickyBar) return;
        let message = this.stickyBar.querySelector('.pdp-sticky-bar__message');
        if (!message) {
          message = document.createElement('p');
          message.className = 'pdp-sticky-bar__message';
          message.setAttribute('role', 'alert');
          this.stickyBar.prepend(message);
        }
        message.textContent = text;
        message.hidden = false;
      }

      hideStickyMessage() {
        const message = this.stickyBar?.querySelector('.pdp-sticky-bar__message');
        if (message) message.hidden = true;
      }

      render() {
        const full = this.cartFull;
        const mainButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`) || this.mainButton;
        const label = mainButton.querySelector('span');
        if (label && !mainButton.hasAttribute('disabled')) {
          label.textContent = full ? this.dataset.labelInCart : this.dataset.labelAdd;
        }
        if (this.stickyButton) {
          this.stickyButton.textContent = full ? this.dataset.labelInCartShort : this.dataset.labelAdd;
          this.stickyButton.classList.toggle('mg-in-cart', full);
        }
        mainButton.classList.toggle('mg-in-cart', full);
        // "Kjøp nå" only shows while the chair can be bought.
        const buyNow = document.getElementById(`ProductBuyNow-${this.dataset.sectionId}`);
        if (buyNow) buyNow.hidden = mainButton.hasAttribute('disabled') && !full;
      }
    }
  );
}
